import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Custom Cabinetry',
    description: 'Handcrafted cabinets tailored to your space with premium materials and expert craftsmanship.'
  },
  {
    title: 'Professional Appliances',
    description: 'Top-tier appliances from renowned brands, professionally installed for optimal performance.'
  },
  {
    title: 'Luxury Countertops',
    description: 'Premium stone surfaces including marble, quartzite, and exotic granites from exclusive quarries.'
  },
  {
    title: 'Smart Integration',
    description: 'Cutting-edge smart home technology for lighting, appliances, and environmental controls.'
  },
  {
    title: 'Custom Storage',
    description: 'Innovative storage solutions designed for your specific needs and cooking style.'
  },
  {
    title: 'Designer Lighting',
    description: 'Layered lighting design with premium fixtures for both function and ambiance.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale Chef\'s Kitchen'
  },
  {
    url: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Modern Kitchen'
  },
  {
    url: 'https://images.unsplash.com/photo-1556909190-10e0da89e124?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Traditional Kitchen'
  }
];

export function KitchenRenovationPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Luxury Kitchen Renovation in Westchester County | Golden Touch</title>
        <meta name="description" content="Premium kitchen renovation services in Westchester County & Greenwich. Specializing in luxury kitchen remodels with custom cabinetry, professional appliances, and smart integration." />
        <meta name="keywords" content="kitchen renovation Westchester, luxury kitchen remodel Greenwich, custom kitchen design Scarsdale, modern kitchen Rye" />
        <link rel="canonical" href="https://renovationwestchester.com/services/kitchen-renovation-westchester" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Luxury Kitchen Renovation in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Transform your kitchen into a masterpiece of design and functionality. 
                Serving distinguished homes throughout Westchester County and Lower Connecticut with 
                unparalleled craftsmanship and attention to detail.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link to="/consultation" className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900">
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Luxury Kitchen Renovation in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Premium Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Transform Your Kitchen?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Let's discuss your vision for a stunning, functional kitchen. Our expert team is ready 
              to bring your culinary dreams to life with unparalleled craftsmanship and attention to detail.
            </p>
            <Link to="/consultation" className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900">
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}